import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { dispatcher } from 'dispatcher';

import { ITranslateMethod } from '@core/js/hooks/i18n/types';

import { RECAPTCHA_ERROR_CODE } from '@interlayer/js/app/components/main/ReCaptchaWithTitle/constants';
import { EVENT_UPGRADE_MEMBER_POPUP } from '@interlayer/js/app/components/main/Registration/components/popup/UpgradeMemberPopup/constants';
import { EVENT_RETAIL_ALREADY_CLUB_MEMBER_POPUP } from '@interlayer/js/app/components/main/Registration/components/templates/RetailReg/components/popups/AlreadyClubMemberPopup/constants';
import { EVENT_RETAIL_ALREADY_ONLINE_MEMBER_POPUP } from '@interlayer/js/app/components/main/Registration/components/templates/RetailReg/components/popups/AlreadyOnlineMemberPopup/constants';
import { useIsRetailReg } from '@interlayer/js/app/components/main/Registration/components/templates/RetailReg/hooks/useIsRetailReg';
import { useIsSlotUpgrade } from '@interlayer/js/app/components/main/Registration/components/templates/SlotReg/hooks/useIsSlotUpgrade';
import { ON_BLUR_EMAIL_FIELD, ON_FOCUS_EMAIL_FIELD } from '@interlayer/js/app/components/main/Registration/constants';
import { USER_PHASES } from '@interlayer/js/app/hooks/auth/constants';
import { useCheckAccountBusinessPhase } from '@interlayer/js/app/hooks/user/hooks/useCheckAccountBusinessPhase';
import { useCheckUserNameAvailability } from '@interlayer/js/app/hooks/user/hooks/useCheckUserNameAvailability';
import { useRegistrationSelector } from '@interlayer/js/app/store/hooks/useRegistrationSelector';
import {
    setIsShowReCaptcha,
    setShowPopupAlreadyMember
} from '@interlayer/js/app/store/reducers/Registration/actions';

import { TUseCheckEmailDuplicateProps } from '../types';

const INTERNAL_EMAIL = 'test@playtech.com';

const useCheckAccountBusinessPhaseClubUser = () => {
    const dispatch = useDispatch();

    const handlerNotFoundUser = useCallback(() => {
        dispatch(setShowPopupAlreadyMember(true));
    }, [dispatch]);

    const handlerFoundUser = useCallback((popupData?: Record<string, unknown>) => {
        dispatcher.dispatch(EVENT_UPGRADE_MEMBER_POPUP, popupData);
    }, []);

    return useCheckAccountBusinessPhase({
        handlerNotFoundUser,
        handlerFoundUser
    });
};

const useCheckAccountBusinessPhaseOnlineUser = () => {
    const handlerNotFoundUser = useCallback(() => {
        dispatcher.dispatch(EVENT_RETAIL_ALREADY_CLUB_MEMBER_POPUP);
    }, []);

    const handlerFoundUser = useCallback((email: string) => {
        dispatcher.dispatch(EVENT_RETAIL_ALREADY_ONLINE_MEMBER_POPUP, email);
    }, []);

    const checkAccountBusinessPhase = useCheckAccountBusinessPhase({
        handlerNotFoundUser,
        handlerFoundUser
    });

    return useCallback((email: string) => {
        return checkAccountBusinessPhase({
            email,
            businessPhase: USER_PHASES.online
        });
    }, [checkAccountBusinessPhase]);
};

export const useCheckEmailDuplicate = (
    t: ITranslateMethod,
    { emailNotCheck = '' }: TUseCheckEmailDuplicateProps = {},
    isBlocked?: boolean
) => {
    const isRetailReg = useIsRetailReg();
    const isSlotUpgrade = useIsSlotUpgrade();
    const [isCheckEmail, setIsCheckEmail] = useState(false);
    const listEmail = useRef<{ [key: string]: { emailAvailable: string }}>({});
    const dispatch = useDispatch();
    const checkUserNameAvailability = useCheckUserNameAvailability();
    const { isShowReCaptcha } = useRegistrationSelector();
    const refReCaptchaToken = useRef('');
    const checkAccountBusinessPhaseClubUser = useCheckAccountBusinessPhaseClubUser();
    const checkAccountBusinessPhaseOnlineUser = useCheckAccountBusinessPhaseOnlineUser();

    const handlerShowReCaptcha = useCallback(() => {
        dispatch(setIsShowReCaptcha(true));
    }, [dispatch]);

    useEffect(() => {
        if (isBlocked) {
            return;
        }

        const handlerOnFocus = () => setIsCheckEmail(false);
        const handlerOnBlur = (reCaptchaToken: string) => {
            if (reCaptchaToken) {
                refReCaptchaToken.current = reCaptchaToken;
            }

            setIsCheckEmail(true);
        };

        dispatcher.listen(ON_FOCUS_EMAIL_FIELD, handlerOnFocus);
        dispatcher.listen(ON_BLUR_EMAIL_FIELD, handlerOnBlur);

        return () => {
            dispatcher.removeListener(ON_FOCUS_EMAIL_FIELD, handlerOnFocus);
            dispatcher.removeListener(ON_BLUR_EMAIL_FIELD, handlerOnBlur);
        };
    }, [isBlocked]);

    return useCallback(async (value: string) => {
        if (isShowReCaptcha) {
            return [t('email.duplicate')];
        }

        if (value === emailNotCheck || !isCheckEmail || INTERNAL_EMAIL === value) {
            return;
        }

        const cachedEmailData = listEmail.current[value];

        if (cachedEmailData) {
            if (cachedEmailData.emailAvailable === 'unavailable') {
                return [t('email.duplicate')];
            }

            return;
        }

        try {
            const response = await checkUserNameAvailability(value, refReCaptchaToken.current);

            if (response.data) {
                listEmail.current = {
                    ...listEmail.current,
                    [value]: response.data
                };
            }

            if (response.data?.emailAvailable === 'unavailable') {
                if (isSlotUpgrade) {
                    return [t('email.duplicate')];
                }

                if (isRetailReg) {
                    checkAccountBusinessPhaseOnlineUser(value);
                    return [t('email.duplicate')];
                }

                checkAccountBusinessPhaseClubUser({ email: value });

                return [t('email.duplicate')];
            }
        } catch (error: any) {
            if (error?.response?.data?.errorCode === RECAPTCHA_ERROR_CODE) {
                handlerShowReCaptcha();
            }
        }
    }, [
        checkAccountBusinessPhaseClubUser, checkAccountBusinessPhaseOnlineUser, checkUserNameAvailability, emailNotCheck,
        handlerShowReCaptcha, isCheckEmail, isRetailReg, isShowReCaptcha, isSlotUpgrade, t
    ]);
};
